/**
 * Keep in sync with server/src/constants/index.ts (PRODUCT_TERM_YEARS,
 * MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT) and server/src/utils/productTermPricing.ts.
 */
import type { ApiSubItem } from '@/lib/sales-metrics';

export const MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT = 50;

/**
 * Contract term options for an ADDITIONAL ARN, chosen per ARN row.
 *
 * Mirror of ADDITIONAL_ARN_TERM_YEARS in server/src/constants/index.ts. Kept
 * apart from PRODUCT_TERM_YEARS because the term belongs to the ARN, not to
 * the ADD_ARN product line - one line covers several ARNs, each bought for its
 * own length. The years are NOT discounted: the fee is a flat registration
 * charge per ARN per year, so three years is exactly three times one.
 */
export const ADDITIONAL_ARN_TERM_YEARS: readonly number[] = [1, 2, 3, 4, 5];

/** Clamps a term to an allowed option, defaulting to 1 for anything unusable. */
export function parseAdditionalArnTermYears(value: unknown): number {
  const parsed = Math.floor(Number(value));
  return ADDITIONAL_ARN_TERM_YEARS.includes(parsed) ? parsed : 1;
}

/** Keep in sync with server/src/constants/index.ts (AUM_PRODUCT_TERM_YEARS). */
export const AUM_PRODUCT_TERM_YEARS: number[] = [1, 2, 3, 4, 5];

export const PRODUCT_TERM_YEARS: Record<string, number[]> = {
  MFTOOLS: [1, 2, 3, 4, 5],
  MFBOX_SILVER: [1, 2, 3, 4, 5],
  MFBOX_GOLD: [1, 2, 3, 4, 5],
  MFBOX_COMBO: [1, 2, 3, 4, 5],
  SSL: [1, 2, 3, 5],
  KYC: [1, 2, 3, 5],
  API: [1, 2, 3, 5],
  ADD_TXN: [1, 2, 3, 5],
  SUB_BROKER: [1, 2, 3, 5],
  SOFTWARE: [1, 2, 3, 5],
  APP_ANDROID: [1, 2, 3, 5],
  APP_IOS: [1, 2, 3, 5],
  WEBSITE_STD: [1],
  WEBSITE_MID: [1],
  WEBSITE_ADV: [1],
  WEBSITE_CUSTOM: [1],
  // SYNC: mirror of server/src/constants/index.ts. Unlike the four tiers
  // above (single price, chosen from admin-set price chips), this one is
  // priced per YEAR like an ordinary flat product - 1/2/3 year terms at
  // exactly 1x/2x/3x the annual rate (its catalogue multiYearDiscountPercent
  // default is 0%).
  WEBSITE_AI: [1, 2, 3],
  MFTOOLS_OLD: [1],
  API_MF_RESEARCH: [1],
  API_CALCULATOR: [1],
  NON_ARN_PRODUCTS: [1],
};

export const WEBSITE_PRODUCT_CODES = [
  'WEBSITE_STD',
  'WEBSITE_MID',
  'WEBSITE_ADV',
  'WEBSITE_CUSTOM',
] as const;

export type WebsiteProductCode = (typeof WEBSITE_PRODUCT_CODES)[number];

// Seeded defaults only - an admin-added tier has no entry here and takes its
// options from Product Settings, so every read must tolerate a miss.
export const WEBSITE_PRICE_SLABS: Record<string, number[]> = {
  WEBSITE_STD: [10000, 15000, 20000, 30000],
  WEBSITE_MID: [40000, 50000, 60000],
  WEBSITE_ADV: [75000, 100000, 125000, 150000],
  WEBSITE_CUSTOM: [],
};

/**
 * Website tiers are identified by their code PREFIX, not a fixed list.
 *
 * SYNC: mirror of isWebsiteProductCode in server/src/utils/productTermPricing.ts.
 * An admin can add a tier (WEBSITE_PREMIUM, say) and it must behave like the
 * shipped four - one live website at a time, price chosen from admin-set
 * options. The legacy 'WEBSITE' code has no underscore and is deliberately not
 * matched.
 */
export const WEBSITE_PRODUCT_CODE_PREFIX = 'WEBSITE_';

export function isWebsiteCode(code: string): boolean {
  return code.startsWith(WEBSITE_PRODUCT_CODE_PREFIX);
}

export const MFTOOLS_OLD_CODE = 'MFTOOLS_OLD';
export const MFTOOLS_OLD_PRICE_SLABS = [5000, 5084.75, 6000, 7000] as const;

// NON_ARN_PRODUCTS deliberately reuses the whole API-bundle mechanism (modal
// select, metadata storage, quotation expansion, admin item management).
export const API_BUNDLE_PRODUCT_CODES = [
  'API_MF_RESEARCH',
  'API_CALCULATOR',
  'NON_ARN_PRODUCTS',
] as const;
export type ApiBundleProductCode = (typeof API_BUNDLE_PRODUCT_CODES)[number];

export const NON_ARN_PRODUCTS_CODE = 'NON_ARN_PRODUCTS';

export function isApiBundleProductCode(code: string): code is ApiBundleProductCode {
  return (API_BUNDLE_PRODUCT_CODES as readonly string[]).includes(code);
}

export interface TermYearsOptions {
  isAumBased?: boolean;
}

export function getAllowedTermYears(productCode: string, options?: TermYearsOptions): number[] {
  if (options?.isAumBased) {
    return AUM_PRODUCT_TERM_YEARS;
  }
  return PRODUCT_TERM_YEARS[productCode] ?? [1];
}

// A website tier priced by TERM (1/2/3 years, flat products.price x years -
// currently only WEBSITE_AI), as opposed to the slab-selected tiers
// (WEBSITE_STD/MID/ADV/CUSTOM) whose price is chosen from admin-set chip
// options. SYNC: mirrors isTermBasedWebsiteCode in
// server/src/utils/productTermPricing.ts.
export function isTermBasedWebsiteCode(productCode: string): boolean {
  return isWebsiteCode(productCode) && getAllowedTermYears(productCode).length > 1;
}

/** Only Custom Development allows free-text price entry in the quotation. */
export function canEditProductPriceInQuotation(productCode: string): boolean {
  if (productCode === 'WEBSITE_CUSTOM') return true;
  if (isWebsiteCode(productCode)) return false;
  const allowedTerms = getAllowedTermYears(productCode);
  if (allowedTerms.length > 1) return false;
  return true;
}

export function getDefaultTermYears(productCode: string, options?: TermYearsOptions): number {
  return getAllowedTermYears(productCode, options)[0] ?? 1;
}

/**
 * Clamps a multi-year discount to a usable percentage.
 *
 * The figure reaches pricing from three places - the admin setting, a per-line
 * override typed by the RM, and a persisted product_details row - so it is
 * normalised in ONE place rather than trusted at each. Anything unusable falls
 * back to the caller's default instead of silently pricing at 0% or 100%.
 *
 * SYNC: mirror of the same helper in server/src/utils/productTermPricing.ts.
 */
export function clampMultiYearDiscountPercent(value: unknown, fallback: number): number {
  const percent = Number(value);
  if (!Number.isFinite(percent)) return fallback;
  return Math.min(100, Math.max(0, Math.round(percent * 100) / 100));
}

/**
 * The multi-year discount that applies to ONE line.
 *
 * A per-line override wins over the company-wide setting: the same product can
 * legitimately go out at the standard 50% to one client and at a negotiated
 * figure to the next, and the quotation is where that is decided.
 */
export function resolveMultiYearDiscountPercent(
  productCode: string,
  multiYearDiscounts: Record<string, number> | undefined,
  fallback: number,
): number {
  const override = multiYearDiscounts?.[productCode];
  if (override === undefined || override === null) return fallback;
  return clampMultiYearDiscountPercent(override, fallback);
}

/**
 * Rounds a money amount to 4 decimal places (ADKJ-124).
 *
 * SYNC: mirror of the same helper in server/src/utils/productTermPricing.ts.
 * NOT whole rupees: products.price moved from INTEGER to DECIMAL(14,4) so a
 * product can carry a fractional-rupee rate (e.g. "Website (AI based)" at
 * Rs 3,093.2203/year, chosen so the GST-inclusive total lands exactly on
 * Rs 3,650 / 7,300 / 10,950 across 1/2/3-year terms). Every other product's
 * price is a whole number, and rounding one to 4dp is a no-op, so this is
 * safe everywhere it replaces `Math.round`.
 */
export function roundMoney(value: number): number {
  return Math.round((Number(value) || 0) * 10_000) / 10_000;
}

export function calculateFlatProductTermTotal(
  annualPrice: number,
  termYears: number,
  renewalDiscountPercent: number = MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT,
): number {
  const safeAnnual = Math.max(0, roundMoney(annualPrice));
  const safeYears = Math.max(1, Math.floor(termYears));
  if (safeYears === 1) {
    return safeAnnual;
  }
  const renewalYearPrice = roundMoney(safeAnnual * (1 - renewalDiscountPercent / 100));
  return safeAnnual + renewalYearPrice * (safeYears - 1);
}

export function calculateMfboxTermTotal(
  setupPrice: number,
  renewalPrice: number,
  termYears: number,
): number {
  const safeSetup = Math.max(0, Math.round(setupPrice));
  const safeRenewal = Math.max(0, Math.round(renewalPrice));
  const safeYears = Math.max(1, Math.floor(termYears));
  if (safeYears === 1) {
    return safeSetup;
  }
  return safeSetup + safeRenewal * (safeYears - 1);
}

export function resolveProductUnitPrice(
  productCode: string,
  catalogPrice: number,
  customPrices: Record<string, number>,
): number {
  if (customPrices[productCode] !== undefined) {
    return customPrices[productCode];
  }
  return catalogPrice;
}

export function resolveProductLineTotal(
  productCode: string,
  catalogPrice: number,
  customPrices: Record<string, number>,
  productTerms: Record<string, number>,
  renewalDiscountPercent: number = MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT,
  multiYearDiscounts: Record<string, number> = {},
): number {
  const unitPrice = resolveProductUnitPrice(productCode, catalogPrice, customPrices);
  const termYears = productTerms[productCode] ?? getDefaultTermYears(productCode);
  const percent = resolveMultiYearDiscountPercent(
    productCode,
    multiYearDiscounts,
    renewalDiscountPercent,
  );
  return calculateFlatProductTermTotal(unitPrice, termYears, percent);
}

export type LineDiscountMode = 'amount' | 'percent';

// Normalizes a raw discount value to the precision it is stored at, so the live
// UI calc, the server quote, and the persisted product_details row can never
// disagree (ADR-036): a percentage keeps 2 decimals (DECIMAL(10,2) column); a
// fixed amount is whole rupees (ADR-004). Keep in sync with the server copy in
// server/src/utils/productTermPricing.ts.
export function normalizeDiscountValue(
  mode: LineDiscountMode | null | undefined,
  value: number | null | undefined,
): number {
  const safe = Number(value) || 0;
  if (safe <= 0) return 0;
  return mode === 'percent' ? Math.round(safe * 100) / 100 : Math.round(safe);
}

// Per-product discount (see ADR-019): a percentage is taken off that product's
// own gross line total; a fixed amount is capped at the line total so a line can
// never go negative. Returns whole rupees. Keep in sync with the server copy in
// server/src/utils/productTermPricing.ts.
export function resolveLineDiscountAmount(
  mode: LineDiscountMode | null | undefined,
  value: number | null | undefined,
  grossLineTotal: number,
): number {
  const safeValue = Number(value) || 0;
  const base = Math.max(0, roundMoney(grossLineTotal));
  if (!mode || safeValue <= 0 || base <= 0) return 0;
  if (mode === 'percent') {
    return roundMoney((base * Math.min(safeValue, 100)) / 100);
  }
  return roundMoney(Math.min(safeValue, base));
}

export function formatProductLabel(
  name: string,
  productCode: string,
  productTerms: Record<string, number>,
): string {
  const years = productTerms[productCode] ?? getDefaultTermYears(productCode);
  if (years > 1) {
    return `${name} (${years}-year term)`;
  }
  return name;
}

// Keep in sync with server/src/constants/index.ts (API_MF_RESEARCH_ITEMS).
export const API_MF_RESEARCH_ITEMS: ApiSubItem[] = [
  { code: 'mfr_1', name: 'Mutual Fund Trailing Returns', price: 4000 },
  { code: 'mfr_2', name: 'Mutual Fund Annual Returns', price: 4000 },
  { code: 'mfr_3', name: 'Mutual Fund Mly/Qly Returns', price: 4000 },
  { code: 'mfr_4', name: 'Trailing Return with Market Cap breakup', price: 4000 },
  { code: 'mfr_5', name: 'Mutual Funds Performance Comparison', price: 4000 },
  { code: 'mfr_6', name: 'Point to Point Returns - Multiple Schemes & Periods', price: 4000 },
  { code: 'mfr_7', name: 'Performance - All Funds in a category / AMC', price: 4000 },
  { code: 'mfr_8', name: 'Consistent Performing Mutual Funds', price: 4000 },
  { code: 'mfr_9', name: 'Mutual Fund Category Monitor', price: 4000 },
  { code: 'mfr_10', name: 'Mutual Fund Benchmark Monitor', price: 4000 },
  { code: 'mfr_11', name: 'Benchmark Lump Sum and SIP Returns', price: 4000 },
  { code: 'mfr_12', name: 'Benchmark Rolling Return', price: 4000 },
  { code: 'mfr_13', name: 'Benchmark Rolling Return - Multiple Benchmark', price: 4000 },
  { code: 'mfr_14', name: 'Benchmark SWP calculator', price: 4000 },
  { code: 'mfr_15', name: 'Benchmark STP calculator', price: 4000 },
  { code: 'mfr_16', name: 'Mutual Fund SIP Returns', price: 4000 },
  { code: 'mfr_17', name: 'SIP Return Calculator', price: 4000 },
  { code: 'mfr_18', name: 'SIP Return Calculator - AMC Wise', price: 4000 },
  { code: 'mfr_19', name: 'SIP with Annual Increase', price: 4000 },
  { code: 'mfr_20', name: 'STP Return Calculator', price: 4000 },
  { code: 'mfr_21', name: 'STP Calculator Profit Transfer', price: 4000 },
  { code: 'mfr_22', name: 'STP with Annual Increase', price: 4000 },
  { code: 'mfr_23', name: 'Top SWP Funds', price: 4000 },
  { code: 'mfr_24', name: 'SWP Return Calculator', price: 4000 },
  { code: 'mfr_25', name: 'SWP with Annual Increase', price: 4000 },
  { code: 'mfr_26', name: 'SWP with Multiple Schemes', price: 4000 },
  { code: 'mfr_27', name: 'Mutual Fund Lump sum Returns', price: 4000 },
  { code: 'mfr_28', name: 'Lumpsum Return Calculator', price: 4000 },
  { code: 'mfr_29', name: 'Lumpsum Return Calculator - AMC Wise', price: 4000 },
  { code: 'mfr_30', name: 'Rolling Return vs Category', price: 8000 },
  { code: 'mfr_31', name: 'Rolling Return vs Benchmark', price: 8000 },
  { code: 'mfr_32', name: 'Rolling Return vs Other Benchmark', price: 8000 },
  { code: 'mfr_33', name: 'Rolling Return vs Sensex & Gold', price: 8000 },
  { code: 'mfr_34', name: 'Debt Fund Maturity and YTM', price: 4000 },
  { code: 'mfr_35', name: 'Debt Fund Credit Quality', price: 4000 },
  { code: 'mfr_36', name: 'Mutual Fund Quartile Ranking', price: 4000 },
  { code: 'mfr_37', name: 'Mutual Fund Volatility Ranking', price: 4000 },
  { code: 'mfr_38', name: 'Top and bottom quartiles performance by category', price: 4000 },
  { code: 'mfr_39', name: 'Mutual Fund risk adjusted returns ranking', price: 4000 },
  { code: 'mfr_40', name: 'Mutual Fund Category Returns', price: 4000 },
  { code: 'mfr_41', name: 'Market Capture Ratio', price: 4000 },
  { code: 'mfr_42', name: 'MF Comparison with Asset Classes', price: 4000 },
  { code: 'mfr_43', name: 'PPF vs ELSS', price: 4000 },
  { code: 'mfr_44', name: 'Mutual Fund Information', price: 4000 },
  { code: 'mfr_45', name: 'Fund Screener', price: 4000 },
  { code: 'mfr_46', name: 'Mutual Fund Selector', price: 4000 },
  { code: 'mfr_47', name: 'Mutual Fund Portfolio Overlap', price: 8000 },
  { code: 'mfr_48', name: 'Common Stocks Across MF Schemes', price: 4000 },
  { code: 'mfr_49', name: 'Mutual Fund Portfolio', price: 12000 },
  { code: 'mfr_50', name: 'Highest Dividend Paying Funds', price: 4000 },
  { code: 'mfr_51', name: 'Consistent Dividend Paying Funds', price: 4000 },
  { code: 'mfr_52', name: 'Categorywise Dividends', price: 4000 },
  { code: 'mfr_53', name: 'Dividend comparison of schemes', price: 4000 },
  { code: 'mfr_54', name: 'Historical Dividends', price: 4000 },
  { code: 'mfr_55', name: 'Latest Dividends', price: 4000 },
  { code: 'mfr_56', name: 'Dividend Paying Fund Returns', price: 4000 },
  { code: 'mfr_57', name: 'Amc Wise Dividends', price: 4000 },
  { code: 'mfr_58', name: 'NAV Change', price: 6000 },
  { code: 'mfr_59', name: 'Mutual Fund Aum By Amc', price: 6000 },
  { code: 'mfr_60', name: 'Mutual Fund Aum By Fund', price: 6000 },
  { code: 'mfr_61', name: 'Factsheet', price: 6000 },
  { code: 'mfr_62', name: 'Benchmark NAV', price: 12000 },
  { code: 'mfr_63', name: 'Scheme information lite API', price: 12000 },
  { code: 'mfr_64', name: 'Standard Deviation', price: 6000 },
  { code: 'mfr_65', name: 'All Scheme Categories', price: 4000 },
  { code: 'mfr_66', name: 'All Schemes by category', price: 4000 },
  { code: 'mfr_67', name: 'Mutual Fund Latest NAV', price: 4000 },
  { code: 'mfr_68', name: 'Historical NAV', price: 36000 },
  { code: 'mfr_69', name: 'Latest NAV vs 52 Week NAV', price: 4000 },
];

// Keep in sync with server/src/constants/index.ts (API_CALCULATOR_ITEMS).
export const API_CALCULATOR_ITEMS: ApiSubItem[] = [
  { code: 'calc_1', name: 'Become A Crorepati', price: 3000 },
  { code: 'calc_2', name: 'Systematic Investment Plan Calculator', price: 3000 },
  { code: 'calc_3', name: 'Mutual Fund Sip Calculator Step Up', price: 3000 },
  { code: 'calc_4', name: 'Goal based Top Up SIP', price: 3000 },
  { code: 'calc_5', name: 'Target Amount SIP Calculator', price: 3000 },
  { code: 'calc_6', name: 'SIP with Annual increase', price: 3000 },
  { code: 'calc_7', name: 'Provident Fund Calculator', price: 3000 },
  { code: 'calc_8', name: 'Employees Provident Fund Calculator', price: 3000 },
  { code: 'calc_9', name: 'Retirement Planning Calculator', price: 3000 },
  { code: 'calc_10', name: 'Asset Allocation', price: 3000 },
  { code: 'calc_11', name: 'Home Loan EMI Calculator', price: 3000 },
  { code: 'calc_12', name: 'Personal Loan EMI Calculator', price: 3000 },
  { code: 'calc_13', name: 'Car Loan EMI Calculator', price: 3000 },
  { code: 'calc_14', name: 'Education Loan EMI Calculator', price: 3000 },
  { code: 'calc_15', name: 'Goal Setting Calculator', price: 3000 },
  { code: 'calc_16', name: 'Composite Financial Goal Planner Calculator', price: 3000 },
  { code: 'calc_17', name: 'Cost Inflation Index', price: 3000 },
  { code: 'calc_18', name: 'Compounding Calculator', price: 3000 },
  { code: 'calc_19', name: 'Spending Less Calculator', price: 3000 },
  { code: 'calc_20', name: 'Future Value Inflation Calculator', price: 3000 },
  { code: 'calc_21', name: 'Human Life Value Calculator', price: 3000 },
  { code: 'calc_22', name: 'Lumpsum Target Calculator', price: 3000 },
  { code: 'calc_23', name: 'Lumpsum Calculator', price: 3000 },
  { code: 'calc_24', name: 'Children Education Planner', price: 3000 },
  { code: 'calc_25', name: 'Networth Calculator', price: 3000 },
  { code: 'calc_26', name: 'SWP Calculator', price: 3000 },
  { code: 'calc_27', name: 'Spending Less Calculator (Advanced)', price: 3000 },
];

// Keep in sync with server/src/constants/index.ts (NON_ARN_PRODUCT_ITEMS).
// Default prices are 0 - the admin sets real prices in Product Settings.
export const NON_ARN_PRODUCT_ITEMS: ApiSubItem[] = [
  { code: 'na_1', name: 'Annual Contract', price: 0 },
  { code: 'na_2', name: 'NFO Marketing Campaign', price: 0 },
  { code: 'na_3', name: 'Email Marketing', price: 0 },
  { code: 'na_4', name: 'Content Writing', price: 0 },
  { code: 'na_5', name: 'Digital & Other Marketing', price: 0 },
  { code: 'na_6', name: 'Banner AD Display', price: 0 },
  { code: 'na_7', name: 'Banner AD Display, Email & Other Marketing Activities', price: 0 },
  { code: 'na_8', name: 'AI Module', price: 0 },
  { code: 'na_9', name: 'Research Data', price: 0 },
  { code: 'na_10', name: 'Tools and Calculator Development', price: 0 },
];

export function getApiSubItems(code: string): ApiSubItem[] {
  if (code === 'API_MF_RESEARCH') return API_MF_RESEARCH_ITEMS;
  if (code === 'API_CALCULATOR') return API_CALCULATOR_ITEMS;
  if (code === 'NON_ARN_PRODUCTS') return NON_ARN_PRODUCT_ITEMS;
  return [];
}
